import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Award, Briefcase, Palette, Heart, MapPin } from "lucide-react";
import { LINKS, ASSETS } from "../lib/constants";

const SectionLabel = ({ children, dark = false, onYellow = false }) => (
  <span className={`section-label ${dark ? "on-dark" : ""} ${onYellow ? "on-yellow" : ""}`}>{children}</span>
);

const Value = ({ title, body }) => (
  <div className="border-t-2 border-brand-black pt-5">
    <h4 className="font-display text-2xl mb-2">{title}</h4>
    <p className="text-sm leading-relaxed text-brand-stone">{body}</p>
  </div>
);

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-white pt-12 pb-20 lg:pt-16 lg:pb-28">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative aspect-[4/5] max-w-[460px] mx-auto lg:mx-0">
              <div className="absolute inset-0 translate-x-3 translate-y-3 bg-brand-yellow rounded-[10px]" />
              <div className="absolute inset-0 -translate-x-2 -translate-y-2 rounded-[10px] overflow-hidden border-[3px] border-brand-black">
                <img src={ASSETS.aboutHero} alt="Magdalena Mahdy" className="w-full h-full object-cover" data-testid="about-portrait" />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2 reveal">
            <SectionLabel>About</SectionLabel>
            <h1 className="font-display text-[clamp(2.4rem,5.6vw,4.6rem)] mt-6 leading-[0.95]">
              Hi, I'm <span className="hi-yellow">Magdalena.</span><br />
              I help business owners stop being Profitable But Broke.
            </h1>
            <p className="mt-7 text-lg sm:text-xl leading-relaxed text-brand-ink max-w-3xl">
              I'm the founder of ProfitMIND and forecastly — and I help purpose-led business
              owners understand their profit, plan their cashflow and make calmer decisions with
              their money. I make the numbers feel safe, useful and human. So that dealing with
              your money becomes less of an overwhelm and more of a powerful driving force in your
              business.
            </p>
            <p className="mt-5 text-lg leading-relaxed text-brand-ink max-w-3xl">
              My work blends practical finance, business strategy and honest conversation, so you
              don't just have numbers in front of you — you actually know what they mean. And I do
              it all through one simple method: <strong>Follow The Cash™</strong>.
            </p>
            <div className="mt-7 flex items-center gap-2 text-sm text-brand-stone">
              <MapPin size={16} className="text-brand-yellow" />
              Based in Essex, UK · Working with founders online
            </div>
          </div>
        </div>
      </section>

      {/* MY STORY */}
      <section className="bg-brand-yellow py-20 lg:py-28 border-y-[1.5px] border-brand-black">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel onYellow>My story</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl leading-[0.95] mb-10 mt-6">
            The short, honest version.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <aside className="md:col-span-5">
              <div className="grid grid-cols-1 gap-4">
                <div className="relative aspect-[3/2]">
                  <div className="absolute inset-0 translate-x-2 translate-y-2 bg-brand-black rounded-[10px]" />
                  <div className="absolute inset-0 -translate-x-1 -translate-y-1 rounded-[10px] overflow-hidden border-[3px] border-brand-black">
                    <img src={ASSETS.photographerCamera} alt="Magdalena as a wedding photographer, camera to her face" className="w-full h-full object-cover" data-testid="about-photographer-1" />
                  </div>
                </div>
                <div className="relative aspect-[3/2]">
                  <div className="absolute inset-0 translate-x-2 translate-y-2 bg-brand-black rounded-[10px]" />
                  <div className="absolute inset-0 -translate-x-1 -translate-y-1 rounded-[10px] overflow-hidden border-[3px] border-brand-black">
                    <img src={ASSETS.photographerWedding} alt="Magdalena shooting a wedding couple" className="w-full h-full object-cover" data-testid="about-photographer-2" />
                  </div>
                </div>
              </div>
            </aside>

            <div className="md:col-span-7 space-y-6 text-lg leading-relaxed text-brand-black">
              <p>
                Before ProfitMIND, there was wedding photography. A very seasonal business. Busy
                months, quieter months, and the constant need to know what was coming before the
                bank balance told the story too late. I was <strong>Profitable But Broke</strong>{" "}
                before I had a name for it.
              </p>
              <p>
                Cashflow forecasting became one of the tools that helped me breathe. It helped me
                plan around seasonal income, especially when thoughts like "Can I survive in the
                quieter winter months?" kept me up at night. But it also helped me make better
                decisions and stopped treating every quiet patch as a disaster. I became calmer
                running my business. It was truly that powerful. But the spreadsheet itself?
                Clunky. Manual. Not exactly the sort of thing most business owners feel excited to
                open.
              </p>
              <p>
                Years later, after mentoring hundreds of founders and startup businesses through
                university partnerships for nearly a decade, I kept seeing the same pattern: super
                capable business owners with brilliant ideas, strong client work — but very little
                calm around their numbers. The same quiet anxiety I'd carried as a photographer.
              </p>
              <p>
                So I retrained in bookkeeping and taxes, built a small practice, but quickly
                realised the work I cared about most wasn't just recording what had already
                happened. It was helping businesses understand what their numbers meant and what
                they were trying to tell them, so they could make better decisions and truly thrive.
              </p>
              <p>
                That's why ProfitMIND exists. That's why I'm building forecastly — the simple
                cashflow forecasting tool I wish I had when I was running my own seasonal business.
                And that's why I'm writing <em>Profitable But Broke</em> — the book that names the
                anxiety most business owners carry silently and gives them a method to fix it
                (coming in 2027/28).
              </p>
              <p>
                <strong>Follow The Cash™</strong> is that method. Four parts. One journey. From
                avoidance to ownership.
              </p>
            </div>
          </div>

          <div className="mt-12 bg-white border-[1.5px] border-brand-black rounded-[10px] p-7 sm:p-9">
            <h4 className="font-display text-2xl mb-6 uppercase">Quick facts</h4>
            <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-3 text-[15px]">
              <li className="flex gap-3"><Award size={18} className="shrink-0 mt-0.5 text-brand-black" /> Qualified bookkeeper &amp; tax accountant</li>
              <li className="flex gap-3"><Award size={18} className="shrink-0 mt-0.5 text-brand-black" /> Entrepreneur-in-Residence &amp; angel investment readiness reviewer</li>
              <li className="flex gap-3"><Briefcase size={18} className="shrink-0 mt-0.5 text-brand-black" /> 13+ years running my own businesses</li>
              <li className="flex gap-3"><Palette size={18} className="shrink-0 mt-0.5 text-brand-black" /> Working artist &amp; ex-wedding photographer</li>
              <li className="flex gap-3"><Briefcase size={18} className="shrink-0 mt-0.5 text-brand-black" /> Nearly a decade mentoring founders &amp; startups via universities and other institutions</li>
              <li className="flex gap-3"><Heart size={18} className="shrink-0 mt-0.5 text-brand-black" /> Cat lover · large coffees · countryside dreaming</li>
            </ul>
          </div>

          <div className="mt-12 pt-10 border-t-2 border-brand-black/15 text-center">
            <p className="font-display text-2xl sm:text-3xl normal-case leading-[1.35] uppercase text-brand-black">
              <span className="block mb-3">Mantra:</span>
              <span className="inline-block bg-white px-4 py-2 rounded-sm">"Profitable But Broke isn't a life sentence. It's a starting point."</span>
            </p>
          </div>
        </div>
      </section>

      {/* WHAT I STAND FOR */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <SectionLabel>What I stand for</SectionLabel>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[0.95]">
                Calm is <span className="hi-yellow">strategic.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-lg leading-relaxed text-brand-ink max-w-xl">
                These principles shape every session, every forecast, every email and every honest
                conversation.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-10">
            <Value title="Clarity" body="Financial visibility without shame. The opposite of fog. The place every better decision begins." />
            <Value title="Empathy" body="Numbers can be emotional. I get it. I name what others often skip, in plain English and without judgement." />
            <Value title="Fun" body="Finance doesn't have to be sexy, but it also doesn't have to feel like punishment. We can make the work lighter." />
            <Value title="Integrity" body="No hustle culture. No revenue-chasing for the sake of it. No cookie-cutter growth plans. Just honest, useful support." />
          </div>
        </div>
      </section>

      {/* WHY I'M KNOWN FOR PROFITABLE BUT BROKE */}
      <section className="bg-brand-black text-white py-20 lg:py-28">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel dark>Why I'm known for "Profitable But Broke"</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[0.95]">
            The quiet confession most business owners are<br />
            <span className="text-brand-yellow">too afraid to make out loud.</span>
          </h2>
          <p className="mt-8 text-lg leading-relaxed">
            If you ask my network what I stand for, it's this: helping founders who are making money
            but can't sleep at night. Profitable But Broke isn't just a phrase. It's the quiet
            confession most business owners are too afraid to make out loud.
          </p>
          <ul className="mt-8 space-y-4 text-lg leading-relaxed">
            <li className="border-l-4 border-brand-yellow pl-5">"I'm making money — but where has it gone?"</li>
            <li className="border-l-4 border-brand-yellow pl-5">"I did my taxes and saw profit — so why is the bank account empty?"</li>
            <li className="border-l-4 border-brand-yellow pl-5">"I hit my revenue target — so why do I feel more stressed than ever?"</li>
          </ul>
          <p className="mt-8 text-lg leading-relaxed">
            I lived it. Then I solved it. Now I teach the method to anyone willing to stop hiding
            and start following the cash. This isn't a side angle. It's the ONE thing I'm known for.
            Everything I build — the book, the software, the advisory, the workshops, the content —
            serves this single mission.
          </p>
        </div>
      </section>

      {/* WHY IT IS DIFFERENT */}
      <section className="bg-brand-yellow py-20 lg:py-28 border-y-[1.5px] border-brand-black">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel onYellow>Why it's different</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[0.95]">
            I bridge three worlds<br />
            most people keep separate.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              { t: "Practical Finance", b: "I can read a profit and loss, balance sheet and cash flow picture — but more importantly, I help you understand what they are saying to you." },
              { t: "Business Strategy", b: "Offers, pricing, capacity, delivery, seasonality and growth decisions. The money only makes sense when we look at the business behind it." },
              { t: "Founder Psychology", b: "Avoidance, shame, overthinking, second-guessing, pressure. These things matter because they shape the decisions you make." },
            ].map((x) => (
              <div key={x.t} className="bg-white border-[1.5px] border-brand-black rounded-[10px] p-7 transition-all hover:-translate-y-1 hover:shadow-[6px_6px_0_#0A0A0A]">
                <h4 className="font-display text-2xl text-brand-black mb-3">{x.t}</h4>
                <p className="text-[15px] text-brand-ink leading-relaxed">{x.b}</p>
              </div>
            ))}
          </div>

          <p className="mt-12 text-lg leading-relaxed text-brand-black">
            ProfitMIND isn't bookkeeping. It isn't a cold finance lecture. It's calm,
            commercially-minded support for the human being leading the business — built around the
            Follow The Cash™ method.
          </p>
        </div>
      </section>

      {/* THE EVERYDAY */}
      <section className="bg-white py-16 lg:py-20" data-testid="about-personal-strip">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative aspect-[4/5] max-w-[480px] mx-auto lg:mx-0">
              <div className="absolute inset-0 translate-x-3 translate-y-3 bg-brand-yellow rounded-[10px]" />
              <div className="absolute inset-0 -translate-x-1 -translate-y-1 rounded-[10px] overflow-hidden border-[3px] border-brand-black">
                <img src={ASSETS.coffeeLaptop} alt="Magdalena working with coffee and laptop" className="w-full h-full object-cover" data-testid="about-coffee-portrait" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <SectionLabel>The everyday</SectionLabel>
            <h3 className="font-display text-3xl sm:text-4xl mt-5 leading-[1] mb-5">
              Working with me feels less corporate, more <span className="hi-yellow">considered.</span>
            </h3>
            <div className="space-y-4 text-[17px] leading-relaxed text-brand-ink">
              <p>
                Large coffees. Honest conversation. A notebook open. A cat occasionally walking
                across the screen. This is how the work actually feels: focused, warm and
                refreshingly human.
              </p>
              <p>
                You won't be shamed for what you haven't looked at yet. You won't be buried in
                jargon. We'll look at what's true, make sense of it together, and decide what needs
                to happen next.
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/work-with-me" className="btn-primary" data-testid="about-work-with-me-btn">
                See how we could work together <ArrowUpRight size={18} />
              </Link>
              <a href={LINKS.tidycal} target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="about-book-btn">
                Book a free 20-min chat
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-yellow border-y-[1.5px] border-brand-black" data-testid="about-final-cta">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h3 className="font-display text-3xl sm:text-4xl uppercase max-w-3xl leading-tight text-brand-black">
            Ready to stop being Profitable But Broke?
          </h3>
          <div className="flex flex-wrap gap-3 shrink-0">
            <a href={LINKS.freeAssessment} target="_blank" rel="noopener noreferrer" data-testid="about-cta-reality-btn" className="btn-primary shadow-w-default">
              Take the free Reality Check <ArrowUpRight size={18} />
            </a>
            <Link to="/work-with-me" data-testid="about-cta-explore-btn" className="btn-secondary">
              Explore ways to work with me
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
